import type {AppThunk} from '../../../../../actions/types'
import {getExtensionEndpoint} from '../../../../../selectors'
import type {SidebarItemId} from '../../../../../selectors/projectTrees'
import {
  getAllProjectsExpandState,
  getAllProjectsTreeHash,
  getDefaultAllProjectsTree,
  getDefaultOverviewTree,
  getOverviewExpandState,
  getOverviewTreeHash,
  matchSidebarItem,
} from '../../../../../selectors/projectTrees'
import {restApi} from '../../../../../services/rest'
import type {ExpandState, ProjectId} from '../../../../../types'
import {stringifyId} from '../../../../../types'
import type {ProjectsTreeItem, SidebarActiveItem} from '../../../../../types/projectTrees'
import type {WritableMap} from '../../../../../utils/object'

import {
  allProjectsExpandState,
  searchProjectsExpandState,
  projectsSidebar,
} from './ProjectsSidebar.slices'

export const toggleExpandAllProjects = (expanded: boolean) =>
  projectsSidebar.actions.changeAllProjectsCollapsed(!expanded)
export const toggleExpandState =
  (
    projectId: ProjectId | undefined,
    expanded: boolean,
    group: 'favorites' | 'all' | 'search' | 'none',
    searchProjectsTreeHash: WritableMap<SidebarItemId, ProjectsTreeItem>,
  ): AppThunk<any> =>
  (dispatch, getState) => {
    const state = getState()
    const data: Array<ExpandState> = [
      {
        type: 'project',
        id: projectId,
        expandState: expanded ? 'EXPANDED' : 'COLLAPSED',
      },
    ]

    if (!expanded) {
      let itemsHash: WritableMap<SidebarItemId, ProjectsTreeItem>

      if (group === 'favorites') {
        itemsHash = getOverviewTreeHash(state)
      } else if (group === 'search') {
        ///
        itemsHash = searchProjectsTreeHash
      } else {
        itemsHash = getAllProjectsTreeHash(state)
      }

      const addChildrenRecursively = (id?: ProjectId) => {
        const item: ProjectsTreeItem | null | undefined = itemsHash
          ? itemsHash.get(`project_${stringifyId(id)}`)
          : null

        if (item?.childrenProjectsIds?.length) {
          item.childrenProjectsIds.forEach(childId => {
            data.push({
              type: 'project',
              id: childId,
              expandState: 'COLLAPSED',
            })
            addChildrenRecursively(childId)
          })
        }
      }

      addChildrenRecursively(projectId)
    }

    if (group === 'search') {
      return dispatch(searchProjectsExpandState.actions.toggle(data))
    }

    if (group === 'all') {
      return dispatch(allProjectsExpandState.actions.toggle(data))
    }

    const extension = getExtensionEndpoint(getState(), 'expandState')

    if (!extension) {
      return Promise.resolve()
    }

    return dispatch(
      restApi.endpoints.updateExpandState.initiate({endpoint: extension.endpoint, data}),
    )
  }
export const expandParents =
  (
    activeItem: SidebarActiveItem,
    searchProjectsTreeHash: WritableMap<SidebarItemId, ProjectsTreeItem>,
  ): AppThunk<any> =>
  (dispatch, getState) => {
    const state = getState()
    const overviewTree = getDefaultOverviewTree(state)?.data
    const isFavorite = !!overviewTree?.some(matchSidebarItem(activeItem))

    const tree = isFavorite ? overviewTree : getDefaultAllProjectsTree(state)?.data
    const expandState = isFavorite
      ? getOverviewExpandState(state)
      : getAllProjectsExpandState(state)

    if (tree == null || expandState == null) {
      return
    }

    const expandSelfAndParents = (item: SidebarActiveItem) => {
      const treeItem = tree.find(matchSidebarItem(item))

      if (treeItem == null || treeItem.group === 'none') {
        return
      }

      if (item.nodeType === 'project' && item !== activeItem) {
        if (expandState[item.id!]?.expandState !== 'EXPANDED') {
          dispatch(toggleExpandState(item.id, true, treeItem.group, searchProjectsTreeHash))
        }
      }

      if (treeItem.parentId != null) {
        expandSelfAndParents({
          nodeType: 'project',
          id: treeItem.parentId,
          group: treeItem.group,
        })
      }
    }

    expandSelfAndParents(activeItem)
  }

export const changeSidebarSearchQuery = projectsSidebar.actions.changeSearchQuery
export const changeSidebarSearchActive = projectsSidebar.actions.changeSearchActive
